import { useCallback, useEffect, useMemo, useState } from 'react';
import { CPDService } from '../../../../services/cpd.service';
import { useSession } from '../../../../hooks/use-session.hook';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import { PROFILES } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../../hooks/use-request.hook';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Icon from '../../../../components/forms/icon.component';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import Footer from '../../../../components/layout/footer.component';
import FilterBar from '../../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../../components/cards/header-stats.component';
import TableCPDRoutes from './table-routes';
import CpdEntityModal from '../shared/cpd-entity.modal';
import CpdReloadButton from '../shared/cpd-reload.button';
import CpdResolvePreviewModal from '../shared/cpd-resolve-preview.modal';

const CPDRoutes: React.FC = () => {
  useBgPages();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const { isAdmin, can } = usePermissions();
  const canWrite = can('cpd.write');
  const canReload = can('cpd.reload');
  const isRoot = session?.profile === PROFILES.SUPER_ADMIN;

  const [routesRaw, setRoutesRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [showModal, setShowModal] = useState<{ show: boolean; data: any; duplicate: boolean }>({
    show: false,
    data: null,
    duplicate: false
  });
  const [preview, setPreview] = useState<{ show: boolean; initial?: any }>({ show: false });

  const routeRows = useMemo<any[]>(() => {
    if (!Array.isArray(routesRaw)) return [];
    return routesRaw.map((item: any) => ({
      id: item.id,
      routeGroupId: item.routeGroupId ?? '—',
      carrier: item.carrier ?? 'PRUEBA / GENÉRICO',
      carrierRaw: item.carrier ?? '',
      enabled: item.enabled ?? false,
      templateId: item.templateId ?? '',
      overrides: item.overrides ?? {},
      createdAt: item.createdAt ?? '',
      updatedAt: item.updatedAt ?? '',
      description: item.description ?? '',
      company: item.carrier ?? 'PRUEBA / GENÉRICO',
      channel: 'ROUTE',
      raw: item
    }));
  }, [routesRaw]);

  const carrierOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return routeRows.reduce<FilterOption[]>((acc, r) => {
      if (r.carrier && !seen.has(r.carrier)) {
        seen.add(r.carrier);
        acc.push({ value: r.carrier, label: r.carrier.toUpperCase() });
      }
      return acc;
    }, []);
  }, [routeRows]);

  const statusOptions = useMemo<FilterOption[]>(
    () => [
      { value: 'true', label: 'ACTIVO' },
      { value: 'false', label: 'INACTIVO' }
    ],
    []
  );

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];
    if (carrierOptions.length > 0) {
      defs.push({
        key: 'company',
        label: 'Carrier',
        icon: <Icon icon="ki-office-bag" size="fs-7" />,
        options: carrierOptions,
        multi: false,
        searchable: true
      });
    }
    defs.push({
      key: 'status',
      label: 'Estado',
      icon: <Icon icon="ki-shield-search" size="fs-7" />,
      options: statusOptions,
      multi: false,
      searchable: false
    });
    return defs;
  }, [carrierOptions, statusOptions]);

  const displayRows = useMemo(() => {
    let rows = routeRows;
    if (filters.company) rows = rows.filter((r) => r.carrier === filters.company);
    if (filters.status) {
      const target = filters.status === 'true';
      rows = rows.filter((r) => r.enabled === target);
    }
    return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [routeRows, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => setFilters(next), []);

  const fetchRoutes = useCallback(async () => {
    try {
      const response = await call(() => CPDService.getRoutes());
      setRoutesRaw(response);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteRoute = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = CPDService.deleteRoutes(String(id));
        await AlertService.toastLoading(
          deletePromise,
          'Eliminando ruta...',
          'Ruta eliminada exitosamente.',
          'Ocurrió un error al intentar eliminar la ruta.'
        );
        fetchRoutes();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchRoutes]
  );

  /** Habilitar/deshabilitar sin abrir el modal: PUT con el documento completo. */
  const handleToggleEnabled = useCallback(
    async (row: any) => {
      const next = !row.enabled;
      const ok = await AlertService.question(
        next ? 'Habilitar ruta' : 'Deshabilitar ruta',
        `Grupo de ruta ${row.routeGroupId} (${row.carrier}) quedará ${next ? 'ACTIVO' : 'INACTIVO'}. ${
          next
            ? 'Su política volverá a aplicarse.'
            : 'Su capa se ignorará al resolver la política (no se borra).'
        }`,
        () => Promise.resolve(true),
        'Continuar'
      );
      if (!ok) return;
      try {
        const it = row.raw;
        const p = CPDService.updateRoute(String(row.id), {
          routeGroupId: Number(it.routeGroupId),
          carrier: it.carrier ?? undefined,
          description: it.description ?? undefined,
          enabled: next,
          templateId: it.templateId ?? null,
          overrides: it.overrides ?? {}
        });
        await AlertService.toastLoading(
          p,
          'Actualizando...',
          'Estado actualizado.',
          'No se pudo actualizar el estado.'
        );
        fetchRoutes();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchRoutes]
  );

  const handleCloseModal = useCallback(
    async (refresh: boolean) => {
      if (refresh) await fetchRoutes();
      setShowModal({ show: false, data: null, duplicate: false });
    },
    [fetchRoutes]
  );

  const handleRefresh = useCallback(async () => {
    await fetchRoutes();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [fetchRoutes]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: 'Total Rutas',
                    value: displayRows.length,
                    icon: 'ki-duotone ki-abstract-26 fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: 'Rutas Activas',
                    value: displayRows.filter((r) => r.enabled).length,
                    icon: 'ki-duotone ki-check-circle fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-light-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                      onClick={() => setPreview({ show: true })}
                      title="GET /v1/cpd/resolve"
                    >
                      <Icon icon="ki-magnifier" size="fs-3" />
                      Ver política efectiva
                    </button>
                    {canReload && <CpdReloadButton />}
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                        onClick={() => setShowModal({ show: true, data: null, duplicate: false })}
                      >
                        <Icon icon="ki-plus" size="fs-3" />
                        Agregar Ruta
                      </button>
                    )}
                  </div>
                }
              />
              {routeRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={[]}
                  selectedChannel=""
                  onChannelChange={() => {}}
                />
              )}
              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <TableCPDRoutes
                    rows={displayRows}
                    isLoading={isRequesting}
                    isRoot={isRoot}
                    isAdmin={isAdmin}
                    canWrite={canWrite}
                    onDelete={handleDeleteRoute}
                    onEdit={(row) => setShowModal({ show: true, data: row, duplicate: false })}
                    onDuplicate={(row) => setShowModal({ show: true, data: row, duplicate: true })}
                    onToggleEnabled={handleToggleEnabled}
                    onPreview={(row) =>
                      setPreview({ show: true, initial: { routeGroupId: row.routeGroupId } })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showModal.show && (
        <CpdEntityModal
          kind="route"
          show={showModal.show}
          payload={showModal.data}
          duplicate={showModal.duplicate}
          onRequestClose={handleCloseModal}
        />
      )}
      {preview.show && (
        <CpdResolvePreviewModal
          show={preview.show}
          initial={preview.initial}
          onRequestClose={() => setPreview({ show: false })}
        />
      )}
    </div>
  );
};

export default CPDRoutes;
